import { ROLE_RANK, type TenantRole } from './enums.js';

/**
 * Capability model.
 *
 * Every protected action maps to a capability, and each capability to the
 * minimum role that holds it. Keeping this in shared code means the UI hides
 * exactly what the API refuses.
 */
export const CAPABILITIES = [
  'document:read',
  'document:create',
  'document:edit',
  'document:delete',
  'document:archive',
  'document:publish',
  'document:export',
  'version:read',
  'changeRequest:create',
  'changeRequest:comment',
  'changeRequest:review',
  'changeRequest:merge',
  'changeRequest:decide',
  'template:read',
  'template:manage',
  'member:read',
  'member:manage',
  'tenant:manage',
  'tenant:delete',
  'settings:read',
  'settings:manage',
  'ai:use',
  'audit:read',
] as const;

export type Capability = (typeof CAPABILITIES)[number];

export const CAPABILITY_MIN_ROLE: Record<Capability, TenantRole> = {
  'document:read': 'reader',
  'document:create': 'author',
  'document:edit': 'author',
  'document:delete': 'admin',
  'document:archive': 'admin',
  'document:publish': 'admin',
  'document:export': 'reader',
  'version:read': 'reader',
  'changeRequest:create': 'author',
  'changeRequest:comment': 'reviewer',
  'changeRequest:review': 'reviewer',
  'changeRequest:merge': 'admin',
  'changeRequest:decide': 'admin',
  'template:read': 'reader',
  'template:manage': 'admin',
  'member:read': 'reader',
  'member:manage': 'admin',
  'tenant:manage': 'admin',
  'tenant:delete': 'owner',
  'settings:read': 'reader',
  'settings:manage': 'admin',
  'ai:use': 'author',
  'audit:read': 'admin',
};

export function hasCapability(role: TenantRole | null | undefined, capability: Capability): boolean {
  if (!role) return false;
  const required = CAPABILITY_MIN_ROLE[capability];
  if (!required) return false;
  return ROLE_RANK[role] >= ROLE_RANK[required];
}

export function capabilitiesForRole(role: TenantRole): Capability[] {
  return CAPABILITIES.filter((capability) => hasCapability(role, capability));
}

export function atLeast(role: TenantRole | null | undefined, minimum: TenantRole): boolean {
  if (!role) return false;
  return ROLE_RANK[role] >= ROLE_RANK[minimum];
}
